import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { TrendingUp, CalendarClock, Hourglass, Wallet, FileSpreadsheet, BellRing, Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const naira = (n) => "₦" + Number(n || 0).toLocaleString("en-NG", { maximumFractionDigits: 0 });
const short = (n) => (n >= 1e6 ? `₦${(n / 1e6).toFixed(1)}m` : n >= 1e3 ? `₦${Math.round(n / 1e3)}k` : `₦${n}`);
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—");
const mon = (k) => new Date(`${k}-01T00:00:00`).toLocaleDateString("en-GB", { month: "short" });

const Kpi = ({ icon: Icon, label, value, sub, testid }) => (
    <div className="bg-white border border-ink/10 p-5" data-testid={testid}>
        <div className="flex items-center gap-2 overline text-ink/50"><Icon className="w-3.5 h-3.5" /> {label}</div>
        <div className="mt-2 font-display font-extrabold text-2xl sm:text-3xl text-ink tabular-nums break-all">{value}</div>
        {sub && <div className="mt-1 text-xs text-ink/50">{sub}</div>}
    </div>
);

const List = ({ title, rows, empty, cols, testid, extra }) => (
    <div className="bg-white border border-ink/10" data-testid={testid}>
        <div className="px-5 py-4 border-b border-ink/10 flex items-center justify-between gap-3 flex-wrap">
            <div className="font-display font-bold text-lg">{title}</div>{extra}
        </div>
        <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[480px]">
                <thead><tr>{cols.map((c) => <th key={c.h} className="text-left px-5 py-2.5 text-xs text-ink/50 font-medium">{c.h}</th>)}</tr></thead>
                <tbody>
                    {rows.length === 0 && <tr><td colSpan={cols.length} className="px-5 py-8 text-center text-ink/40">{empty}</td></tr>}
                    {rows.map((r, i) => <tr key={r.company_id + i} className="border-t border-ink/5">{cols.map((c) => <td key={c.h} className="px-5 py-3">{c.v(r)}</td>)}</tr>)}
                </tbody>
            </table>
        </div>
    </div>
);

export default function SuperAdminRevenue() {
    const [d, setD] = useState(null);
    const [win, setWin] = useState(30);
    const [year, setYear] = useState(new Date().getFullYear());
    const [busy, setBusy] = useState("");
    const load = () => api.get("/super-admin/revenue").then(({ data }) => setD(data)).catch(() => toast.error("Could not load revenue"));
    useEffect(() => { load(); }, []);
    if (!d) return <div className="p-10 text-ink/40">Loading…</div>;

    const nudge = async (r) => {
        setBusy(r.company_id);
        try {
            const { data } = await api.post(`/super-admin/billing/${r.company_id}/nudge`);
            toast.success(`Reminder sent to ${r.name}${data.emailed.length ? ` (${data.emailed.join(", ")})` : ""}`);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not send the reminder");
        } finally {
            setBusy("");
        }
    };

    const exportXlsx = async () => {
        setBusy("export");
        try {
            const { data } = await api.get("/super-admin/revenue/export.xlsx", { params: { year }, responseType: "blob" });
            const url = URL.createObjectURL(data);
            Object.assign(document.createElement("a"), { href: url, download: `suntosh_revenue_${year}.xlsx` }).click();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch {
            toast.error("Could not download the export");
        } finally {
            setBusy("");
        }
    };
    const thisYear = new Date().getFullYear();
    const inGrace = d.grace.filter((r) => !r.locked);

    const renewals = d.renewals.filter((r) => r.days_left <= win);
    const company = (r) => <div><div className="font-medium">{r.name}</div><div className="text-xs text-ink/50">{r.company_id} · {r.staff_count} staff</div></div>;
    const tabs = (
        <div className="inline-flex border border-ink/15 rounded-full p-0.5" data-testid="sa-renewals-window">
            {[30, 60, 90].map((w) => (
                <button key={w} onClick={() => setWin(w)} data-testid={`sa-renewals-${w}`}
                    className={`px-3 py-1 text-xs rounded-full transition-colors ${win === w ? "bg-ink text-white" : "text-ink/60 hover:text-ink"}`}>{w} days</button>
            ))}
        </div>
    );

    return (
        <div className="p-6 sm:p-10 max-w-7xl mx-auto" data-testid="sa-revenue-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h1 className="font-display font-extrabold text-4xl text-ink">Revenue</h1>
                    <p className="mt-1 text-sm text-ink/55">Subscription income from Paystack, upcoming renewals and companies still in their grace period.</p>
                </div>
                <div className="flex items-center gap-2" data-testid="sa-revenue-export">
                    <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="border border-ink/15 bg-white px-3 py-2 text-sm" data-testid="sa-revenue-export-year">
                        {Array.from({ length: 5 }, (_, i) => thisYear - i).map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                    <button onClick={exportXlsx} disabled={busy === "export"} className="inline-flex items-center gap-2 bg-ink text-white px-4 py-2 text-sm hover:bg-ink/85 disabled:opacity-60" data-testid="sa-revenue-export-btn">
                        <FileSpreadsheet className="w-4 h-4" /> {busy === "export" ? "Preparing…" : "Download Excel"}
                    </button>
                </div>
            </div>
            <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Kpi icon={Wallet} label="This month" value={naira(d.this_month)} sub={`${d.this_month_count} payment${d.this_month_count === 1 ? "" : "s"}`} testid="sa-kpi-month" />
                <Kpi icon={TrendingUp} label="This year" value={naira(d.this_year)} testid="sa-kpi-year" />
                <Kpi icon={CalendarClock} label="Renewals in 30 days" value={d.renewals.filter((r) => r.days_left <= 30).length}
                    sub={naira(d.renewals.filter((r) => r.days_left <= 30).reduce((s, r) => s + r.price_ngn, 0)) + " expected"} testid="sa-kpi-renewals" />
                <Kpi icon={Hourglass} label="In grace period" value={inGrace.length} sub={naira(inGrace.reduce((s, r) => s + r.price_ngn, 0)) + " if all pay"} testid="sa-kpi-grace" />
            </div>
            <div className="mt-6 bg-white border border-ink/10 p-5" data-testid="sa-revenue-chart">
                <div className="flex items-baseline justify-between"><div className="font-display font-bold text-lg">Monthly income</div><div className="text-xs text-ink/50">All time {naira(d.all_time)}</div></div>
                <div className="mt-4 h-64">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={d.months} margin={{ left: 0, right: 8, top: 8 }}>
                            <CartesianGrid vertical={false} stroke="rgba(26,26,26,0.06)" />
                            <XAxis dataKey="month" tickFormatter={mon} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                            <YAxis tickFormatter={short} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={56} />
                            <Tooltip formatter={(v) => naira(v)} labelFormatter={(k) => new Date(`${k}-01T00:00:00`).toLocaleDateString("en-GB", { month: "long", year: "numeric" })} cursor={{ fill: "rgba(0,47,167,0.06)" }} />
                            <Bar dataKey="total" name="Income" fill="#002FA7" radius={[4, 4, 0, 0]} maxBarSize={36} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
            <div className="mt-6 grid grid-cols-1 xl:grid-cols-2 gap-6">
                <List title="Renewals due" rows={renewals} empty={`No renewals in the next ${win} days`} testid="sa-renewals-list" extra={tabs}
                    cols={[{ h: "Company", v: company }, { h: "Plan", v: (r) => r.plan_name }, { h: "Renews", v: (r) => <span>{fmt(r.paid_until)} <span className="text-xs text-ink/50">· {r.days_left}d</span></span> }, { h: "Amount", v: (r) => <span className="tabular-nums">{naira(r.price_ngn)}</span> }]} />
                <List title="Waiting on payment" rows={d.grace} empty="No companies in grace period" testid="sa-grace-list"
                    cols={[{ h: "Company", v: company }, { h: "Plan needed", v: (r) => r.plan_name },
                        { h: "Grace ends", v: (r) => (r.locked ? <span className="text-red-700 font-semibold">Locked</span> : <span className={r.days_left <= 3 ? "text-red-700 font-semibold" : ""}>{fmt(r.grace_ends_at)} · {r.days_left}d</span>) },
                        { h: "Amount", v: (r) => <span className="tabular-nums">{naira(r.price_ngn)}</span> },
                        { h: "Last reminded", v: (r) => <span className="text-xs text-ink/60 whitespace-nowrap" data-testid={`sa-last-reminded-${r.company_id}`}>{r.last_reminded_at ? fmt(r.last_reminded_at) : "Never"}</span> },
                        { h: "", v: (r) => (
                            <button onClick={() => nudge(r)} disabled={r.nudged_today || busy === r.company_id} data-testid={`sa-nudge-${r.company_id}`}
                                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-klein/30 px-3 py-1 text-xs font-semibold text-klein hover:bg-klein/5 disabled:border-ink/10 disabled:text-ink/40 disabled:hover:bg-transparent">
                                {r.nudged_today ? <><Check className="w-3.5 h-3.5" /> Reminded today</> : <><BellRing className="w-3.5 h-3.5" /> {busy === r.company_id ? "Sending…" : "Send reminder"}</>}
                            </button>
                        ) }]} />
            </div>
            <div className="mt-6">
                <List title="Reminder history" rows={d.reminders || []} empty="No reminders sent yet" testid="sa-reminder-history"
                    cols={[{ h: "Sent", v: (r) => <span className="whitespace-nowrap">{fmt(r.sent_at)}</span> }, { h: "Company", v: (r) => r.name },
                        { h: "Type", v: (r) => <span className={r.manual ? "text-klein font-semibold" : "text-ink/70"}>{r.kind}</span> },
                        { h: "Reached", v: (r) => <span className="text-xs text-ink/60">{r.emailed} email{r.emailed === 1 ? "" : "s"} · {r.pushed} phone{r.pushed === 1 ? "" : "s"}</span> },
                        { h: "Paid afterwards?", v: (r) => (r.paid_at
                            ? <span className="text-emerald-700 font-semibold whitespace-nowrap">Yes · {fmt(r.paid_at)} · {naira(r.paid_amount)}</span>
                            : <span className="text-ink/45">Not yet</span>) }]} />
            </div>
            <div className="mt-6">
                <List title="Latest payments" rows={d.recent} empty="No payments yet" testid="sa-recent-payments"
                    cols={[{ h: "Date", v: (r) => fmt(r.paid_at) }, { h: "Company", v: (r) => r.company_name }, { h: "What", v: (r) => (r.kind === "upgrade" ? `Upgrade to ${r.plan_name}` : `${r.plan_name} · 1 year`) }, { h: "Receipt", v: (r) => <span className="text-xs text-ink/60">{r.receipt_no || "—"}</span> }, { h: "Amount", v: (r) => <span className="tabular-nums font-semibold">{naira(r.amount_ngn)}</span> }]} />
            </div>
        </div>
    );
}
